/**
 * AnimatedMonkey — the Zporti mascot, rigged from separated PNG parts and animated with the
 * built-in React Native `Animated` API. The built-in driver (not Reanimated worklets) keeps
 * it renderable everywhere — native, web, and Storybook — and matches the splash approach.
 *
 * Idle "soul" comes from small, de-synchronised, non-metronomic motions layered together:
 *   • breathe   — the whole body swells (scaleY), forever
 *   • sway      — the head rocks + bobs slowly on the neck (secondary action)
 *   • blink     — irregular blinks incl. an occasional double (the lid drops over the eye)
 *   • wave      — the arm waves in bursts; the head leans into each burst
 *
 * Presentational and self-contained: parts are bundled assets, so it never depends on CDN
 * reachability. Honours the OS "reduce motion" setting (renders the static resting pose).
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { View, Animated, Easing, AccessibilityInfo, type StyleProp, type ViewStyle } from 'react-native';
import { Image } from 'expo-image';
import {
  MONKEY_LAYOUT,
  BREATHE_KEYFRAMES,
  WAVE_KEYFRAMES,
  WAVE_TILT_KEYFRAMES,
  SWAY_ROT_KEYFRAMES,
  SWAY_Y_KEYFRAMES,
  SWAY_PERIOD_MS,
  LID_BLINK_SCALE_X,
  BLINK_SCHEDULE,
  BLINK_TIMING,
  MONKEY_DURATION,
  WAVE_GAP_MS,
  ANIMATED_MONKEY_DEFAULT_SIZE,
  ANIMATED_MONKEY_TEST_ID,
} from './AnimatedMonkey.constants';
import {
  getAnimatedMonkeyFrameStyles,
  monkeyStyles,
  withPivot,
  MONKEY_PIVOT_DELTAS,
} from './AnimatedMonkey.styles';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';

type AnimatedViewStyle = React.ComponentProps<typeof Animated.View>['style'];

/**
 * One monkey layer: an Animated.View carrying the part's rect + (optional) animated
 * transform, with the part image filling it.
 *
 * Loads the bundled part first; if that fails to resolve (e.g. the iOS production profile
 * where bundled require()s don't ship), it falls back to the CDN copy so the mascot still
 * renders. See constants/assets ANIMATED_MONKEY + scripts/check-bundled-assets ALLOWLIST.
 */
function Part({ layout, style }: { layout: { src: number; cdn: string }; style: AnimatedViewStyle }) {
  const [useCdn, setUseCdn] = useState(false);
  const onError = useCallback(() => setUseCdn(true), []);
  return (
    <Animated.View style={style}>
      <Image
        source={useCdn ? { uri: layout.cdn } : layout.src}
        style={monkeyStyles.fill}
        contentFit="fill"
        alt={WEB_IMAGE_ALT_DECORATIVE}
        onError={onError}
      />
    </Animated.View>
  );
}

interface AnimatedMonkeyProps {
  /** Rendered square size in px. Default {@link ANIMATED_MONKEY_DEFAULT_SIZE}. */
  size?: number;
  style?: StyleProp<ViewStyle>;
  /** Screen-reader label; when omitted the mascot is treated as decorative. */
  accessibilityLabel?: string;
  testID?: string;
}

export function AnimatedMonkey({
  size = ANIMATED_MONKEY_DEFAULT_SIZE,
  style,
  accessibilityLabel,
  testID,
}: AnimatedMonkeyProps) {
  const frameStyles = useMemo(() => getAnimatedMonkeyFrameStyles(size), [size]);
  const [reduceMotion, setReduceMotion] = useState(false);

  const breathe = useRef(new Animated.Value(0)).current;
  const sway = useRef(new Animated.Value(0)).current;
  const blink = useRef(new Animated.Value(0)).current;
  const wave = useRef(new Animated.Value(0)).current;

  const animate = !reduceMotion;

  // Respect the OS "reduce motion" setting — render the static resting pose instead.
  useEffect(() => {
    let active = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((enabled) => {
      if (active) setReduceMotion(enabled);
    });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
    return () => {
      active = false;
      sub.remove();
    };
  }, []);

  // Continuous idle loops. Durations are non-harmonic so the motions drift out of phase.
  useEffect(() => {
    if (!animate) return;

    // Breathe: scaleY up then back down, forever.
    const breatheLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(breathe, {
          toValue: 1,
          duration: MONKEY_DURATION.breatheHalf,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(breathe, {
          toValue: 0,
          duration: MONKEY_DURATION.breatheHalf,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]),
    );

    // Sway: one linear 0→1 pass drives a full sinusoidal head rock (0 and 1 are both the
    // neutral pose, so the loop restart is seamless).
    const swayLoop = Animated.loop(
      Animated.timing(sway, {
        toValue: 1,
        duration: SWAY_PERIOD_MS,
        easing: Easing.linear,
        useNativeDriver: false,
      }),
    );

    // Blink: irregular waits between quick lid drops; one entry is a double-blink.
    const blinkOnce = () =>
      Animated.sequence([
        Animated.timing(blink, {
          toValue: BLINK_TIMING.closed,
          duration: BLINK_TIMING.close,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(blink, {
          toValue: 0,
          duration: BLINK_TIMING.open,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
      ]);
    const blinkLoop = Animated.loop(
      Animated.sequence(
        BLINK_SCHEDULE.flatMap((b) => {
          const steps: Animated.CompositeAnimation[] = [Animated.delay(b.wait), blinkOnce()];
          if (b.double) steps.push(Animated.delay(BLINK_TIMING.doubleGap), blinkOnce());
          return steps;
        }),
      ),
      { resetBeforeIteration: false },
    );

    // Wave: burst then a short rest, forever. The second (1 → 1) timing is the between-wave
    // gap; `resetBeforeIteration` (default) snaps back to 0 so each burst re-plays.
    const waveLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(wave, {
          toValue: 1,
          duration: MONKEY_DURATION.wave,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: false,
        }),
        Animated.timing(wave, {
          toValue: 1,
          duration: WAVE_GAP_MS,
          easing: Easing.linear,
          useNativeDriver: false,
        }),
      ]),
    );

    const loops = [breatheLoop, swayLoop, blinkLoop, waveLoop];
    loops.forEach((l) => l.start());
    return () => {
      loops.forEach((l) => l.stop());
      breathe.setValue(0);
      sway.setValue(0);
      blink.setValue(0);
      wave.setValue(0);
    };
  }, [animate, breathe, sway, blink, wave]);

  // Every pivot is an explicit translate sandwich (withPivot) — `transformOrigin` is ignored
  // on native Android once Animated replaces the transform per frame (web honours it).
  const groupAnim = useMemo<AnimatedViewStyle>(
    () => ({
      transform: withPivot(MONKEY_PIVOT_DELTAS.group, [
        { scaleY: breathe.interpolate({ inputRange: [...BREATHE_KEYFRAMES.input], outputRange: [...BREATHE_KEYFRAMES.output] }) },
      ]),
    }),
    [breathe],
  );
  // Head + face rock on the neck; during a wave burst they also lean toward the arm.
  const headAnim = useMemo<AnimatedViewStyle>(
    () => ({
      transform: withPivot(MONKEY_PIVOT_DELTAS.head, [
        { translateY: sway.interpolate({ inputRange: [...SWAY_Y_KEYFRAMES.input], outputRange: [...SWAY_Y_KEYFRAMES.output] }) },
        { rotate: sway.interpolate({ inputRange: [...SWAY_ROT_KEYFRAMES.input], outputRange: [...SWAY_ROT_KEYFRAMES.output] }) },
        { rotate: wave.interpolate({ inputRange: [...WAVE_TILT_KEYFRAMES.input], outputRange: [...WAVE_TILT_KEYFRAMES.output] }) },
      ]),
    }),
    [sway, wave],
  );
  const armAnim = useMemo<AnimatedViewStyle>(
    () => ({
      transform: withPivot(MONKEY_PIVOT_DELTAS.arm, [
        { rotate: wave.interpolate({ inputRange: [...WAVE_KEYFRAMES.input], outputRange: [...WAVE_KEYFRAMES.output] }) },
      ]),
    }),
    [wave],
  );
  // blink drives lid scaleY directly (0 = eye open, closed = lid dropped); a constant scaleX
  // narrows the lid (about its hinge) so the closed eye matches the eye width.
  const lidLAnim = useMemo<AnimatedViewStyle>(
    () => ({
      transform: withPivot(MONKEY_PIVOT_DELTAS.lidL, [
        { scaleX: LID_BLINK_SCALE_X },
        { scaleY: blink },
      ]),
    }),
    [blink],
  );
  const lidRAnim = useMemo<AnimatedViewStyle>(
    () => ({
      transform: withPivot(MONKEY_PIVOT_DELTAS.lidR, [
        { scaleX: LID_BLINK_SCALE_X },
        { scaleY: blink },
      ]),
    }),
    [blink],
  );

  return (
    <View
      style={[frameStyles.frame, style]}
      testID={testID ?? ANIMATED_MONKEY_TEST_ID}
      accessible={accessibilityLabel != null}
      accessibilityRole={accessibilityLabel != null ? 'image' : undefined}
      accessibilityLabel={accessibilityLabel}
      importantForAccessibility={accessibilityLabel != null ? 'yes' : 'no-hide-descendants'}
    >
      <View style={frameStyles.stage}>
        <Animated.View style={[monkeyStyles.group, animate && groupAnim]}>
          <Part layout={MONKEY_LAYOUT.body} style={monkeyStyles.body} />
          <Part layout={MONKEY_LAYOUT.arm} style={[monkeyStyles.arm, animate && armAnim]} />
          <Animated.View style={[monkeyStyles.headGroup, animate && headAnim]}>
            <Part layout={MONKEY_LAYOUT.head} style={monkeyStyles.head} />
            <Part layout={MONKEY_LAYOUT.lidL} style={[monkeyStyles.lidL, animate && lidLAnim]} />
            <Part layout={MONKEY_LAYOUT.lidR} style={[monkeyStyles.lidR, animate && lidRAnim]} />
          </Animated.View>
        </Animated.View>
      </View>
    </View>
  );
}
